/**
 * Installation Progress Step Component
 *
 * Subscribes to batch installation progress via SSE and shows real-time status
 * for each component being installed.
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { getApiBaseUrl } from '@/services/urlResolver';

type InstallStatus = 'pending' | 'installing' | 'complete' | 'error' | 'cancelled';

interface BatchProgressData {
  currentComponent: string;
  componentIndex: number;
  totalComponents: number;
  componentProgress: number;
  overallProgress: number;
  status: InstallStatus;
  message: string;
  completedComponents: string[];
  failedComponents: Record<string, string>;
}

interface InstallationProgressStepProps {
  onNext: () => void;
  onCancel: () => void;
}

// ---------------------------------------------------------------------------
// Icon sub-components
// ---------------------------------------------------------------------------

function CheckCircleIcon(): React.ReactElement {
  return (
    <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function XCircleIcon(): React.ReactElement {
  return (
    <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function PendingCircleIcon(): React.ReactElement {
  return (
    <svg className="w-5 h-5 text-white/20 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16z" clipRule="evenodd" />
    </svg>
  );
}

function SpinnerIcon(): React.ReactElement {
  return (
    <svg
      className="w-5 h-5 text-primary-400 flex-shrink-0 animate-spin"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component checklist row
// ---------------------------------------------------------------------------

type ComponentState = 'pending' | 'active' | 'done' | 'failed';

interface ComponentRowProps {
  name: string;
  state: ComponentState;
  progress: number;
  errorMessage: string | null;
}

function ComponentRow({
  name,
  state,
  progress,
  errorMessage,
}: ComponentRowProps): React.ReactElement {
  const icon: Record<ComponentState, React.ReactElement> = {
    pending: <PendingCircleIcon />,
    active: <SpinnerIcon />,
    done: <CheckCircleIcon />,
    failed: <XCircleIcon />,
  };

  const labelColor: Record<ComponentState, string> = {
    pending: 'text-white/30',
    active: 'text-white/90',
    done: 'text-white/70',
    failed: 'text-red-400',
  };

  return (
    <li className="space-y-1">
      <div className="flex items-center gap-3">
        {icon[state]}
        <span className={`text-sm font-medium flex-1 min-w-0 truncate ${labelColor[state]}`}>
          {name}
        </span>
        {state === 'active' && (
          <span className="text-xs text-white/40 flex-shrink-0 tabular-nums">{progress}%</span>
        )}
      </div>
      {state === 'active' && (
        <div className="ml-8 h-1 bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary-500 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
      {state === 'failed' && errorMessage !== null && (
        <p className="ml-8 text-xs text-red-300/80">{errorMessage}</p>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function InstallationProgressStep({
  onNext,
  onCancel,
}: InstallationProgressStepProps): React.ReactElement {
  const { t } = useTranslation();
  const [progress, setProgress] = useState<BatchProgressData | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/api/v1/setup/install-batch/progress`;
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(String(event.data)) as BatchProgressData;
        setProgress(data);

        if (data.status === 'complete' || data.status === 'error' || data.status === 'cancelled') {
          es.close();
          eventSourceRef.current = null;
        }
      } catch (err) {
        console.error('[InstallationProgressStep] Failed to parse SSE event:', err);
      }
    };

    es.onerror = () => {
      // Only treat as error if we haven't received a terminal status yet
      setProgress((prev) => {
        const terminal =
          prev?.status === 'complete' || prev?.status === 'error' || prev?.status === 'cancelled';
        if (!terminal) {
          setConnectionError(
            t('wizard.installProgress.connectionError', {
              defaultValue: 'Lost connection to installation service.',
            })
          );
        }
        return prev;
      });
      es.close();
      eventSourceRef.current = null;
    };

    return () => {
      es.close();
      eventSourceRef.current = null;
    };
    // t is stable — deliberately excluded to avoid reconnecting on language change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Derive per-component states from progress data
  const componentStates = (progress?.completedComponents ?? []).concat(
    Object.keys(progress?.failedComponents ?? {})
  );

  const isDone =
    progress?.status === 'complete' ||
    progress?.status === 'error' ||
    progress?.status === 'cancelled';

  const isInstalling = progress?.status === 'installing' || progress?.status === 'pending';

  const overallPercent = Math.round(progress?.overallProgress ?? 0);

  // Build summary counts
  const completedCount = progress?.completedComponents.length ?? 0;
  const failedCount = Object.keys(progress?.failedComponents ?? {}).length;
  const skippedCount = (progress?.totalComponents ?? 0) - completedCount - failedCount;

  // Determine component state for a given name
  const getComponentState = (name: string): ComponentState => {
    if (progress?.completedComponents.includes(name)) return 'done';
    if (progress?.failedComponents[name] !== undefined) return 'failed';
    if (progress?.currentComponent === name && !isDone) return 'active';
    return 'pending';
  };

  // Build the full component list from all known names
  const allComponentNames: string[] = [];
  if (progress !== null) {
    const seen = new Set<string>();
    for (const name of progress.completedComponents) {
      if (!seen.has(name)) {
        seen.add(name);
        allComponentNames.push(name);
      }
    }
    for (const name of Object.keys(progress.failedComponents)) {
      if (!seen.has(name)) {
        seen.add(name);
        allComponentNames.push(name);
      }
    }
    if (progress.currentComponent && !seen.has(progress.currentComponent)) {
      seen.add(progress.currentComponent);
      allComponentNames.push(progress.currentComponent);
    }
  }
  // Suppress unused variable warning for componentStates
  void componentStates;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="section-heading-xl">
          {t('wizard.installProgress.title', { defaultValue: 'Setting Up Your Environment' })}
        </h2>
        <p className="text-white/60 text-sm max-w-lg mx-auto">
          {t('wizard.installProgress.subtitle', {
            defaultValue: "This may take a few minutes. Please don't close the application.",
          })}
        </p>
      </div>

      {/* Connection error */}
      {connectionError !== null && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
          <p className="text-sm text-red-300">{connectionError}</p>
        </div>
      )}

      {/* Overall progress bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-white/60">
            {progress?.message ??
              t('wizard.installProgress.waiting', { defaultValue: 'Waiting to start...' })}
          </span>
          <span className="text-sm font-semibold text-white tabular-nums">{overallPercent}%</span>
        </div>
        <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              progress?.status === 'error' ? 'bg-red-500' : 'bg-primary-500'
            }`}
            style={{ width: `${overallPercent}%` }}
          />
        </div>
        {progress !== null && (
          <p className="text-xs text-white/30 text-right tabular-nums">
            {progress.componentIndex} / {progress.totalComponents}
          </p>
        )}
      </div>

      {/* Component checklist */}
      {allComponentNames.length > 0 && (
        <div className="bg-black/20 border border-white/10 rounded-xl p-4">
          <ul className="space-y-3">
            {allComponentNames.map((name) => (
              <ComponentRow
                key={name}
                name={name}
                state={getComponentState(name)}
                progress={
                  progress?.currentComponent === name ? Math.round(progress.componentProgress) : 0
                }
                errorMessage={progress?.failedComponents[name] ?? null}
              />
            ))}
          </ul>
        </div>
      )}

      {/* No data yet — waiting for first event */}
      {progress === null && connectionError === null && (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent mx-auto mb-3" />
          <p className="text-sm text-white/40">
            {t('wizard.installProgress.connecting', { defaultValue: 'Connecting...' })}
          </p>
        </div>
      )}

      {/* Completion summary */}
      {isDone && progress !== null && (
        <div
          className={`rounded-xl p-4 border ${
            failedCount > 0
              ? 'bg-amber-900/20 border-amber-500/30'
              : 'bg-green-900/20 border-green-500/30'
          }`}
        >
          <h3
            className={`font-semibold mb-2 ${
              failedCount > 0 ? 'text-amber-300' : 'text-green-300'
            }`}
          >
            {progress.status === 'cancelled'
              ? t('wizard.installProgress.cancelled', { defaultValue: 'Installation cancelled' })
              : t('wizard.installProgress.done', { defaultValue: 'Installation complete' })}
          </h3>
          <ul className="space-y-1 text-sm">
            <li className="text-green-300/80">
              {t('wizard.installProgress.summary.installed', {
                defaultValue: '{{count}} installed',
                count: completedCount,
              })}
            </li>
            {failedCount > 0 && (
              <li className="text-red-300/80">
                {t('wizard.installProgress.summary.failed', {
                  defaultValue: '{{count}} failed',
                  count: failedCount,
                })}
              </li>
            )}
            {skippedCount > 0 && (
              <li className="text-white/40">
                {t('wizard.installProgress.summary.skipped', {
                  defaultValue: '{{count}} skipped',
                  count: skippedCount,
                })}
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-2">
        <button
          onClick={onCancel}
          disabled={!isInstalling}
          className="px-4 py-2 text-white/50 hover:text-white/80 disabled:opacity-30 disabled:cursor-not-allowed transition-colors min-h-[44px]"
        >
          {t('wizard.installProgress.cancel', { defaultValue: 'Cancel' })}
        </button>
        <button
          onClick={onNext}
          disabled={!isDone}
          className="px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-600/40 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors min-h-[44px]"
        >
          {t('wizard.continue', { defaultValue: 'Continue' })}
        </button>
      </div>
    </div>
  );
}

export default InstallationProgressStep;
